"use client"

import { motion } from "framer-motion"
import { Sword, Scroll, Shield, Target, MapPin, Clock, Award, ChevronLeft } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"

const steps = [
    {
        title: "Talk to Orven",
        description: "After completing all EXALTED QUESTS, your journey begins with High Priest Orven. He will provide you with the necessary instructions to proceed toward the Tower of Insolence.",
        npc: "Orven",
        location: "Aden Temple",
        icon: Scroll,
        color: "amber",
        image: "/guides/cave-of-trials/guide_img_p1_i1.png",
    },
    {
        title: "The Chaos Power",
        description: "Return to Aden Town and seek out Herphah. You will begin the 'Chaos Power: War in Kasha Orcs' quest chain. Follow the path and speak with Hakan in Gainak to continue the sequence.",
        npc: "Herphah / Hakan",
        location: "Aden / Gainak",
        icon: Sword,
        color: "red",
        image: "/guides/cave-of-trials/guide_img_p2_i1.png",
    },
    {
        title: "The Great Hunt",
        description: "Travel to the Immortal Plateau and test your strength. You must defeat 2,000 monsters and collect 500 quest items. Once completed, speak with Dokara to begin the next phase: 'Prisoners'.",
        npc: "Dokara",
        location: "Immortal Plateau",
        icon: Target,
        color: "blue",
        image: "/guides/cave-of-trials/guide_img_p2_i2.png",
    },
    {
        title: "Rescue the Captives",
        description: "You will be granted entry to the Cave of Trials. You have exactly 7 minutes to rescue all prisoners. Swiftness and power are key to success in this solo challenge.",
        npc: "Instance",
        location: "Cave of Trials",
        icon: Shield,
        color: "green",
        image: "/guides/cave-of-trials/guide_img_p4_i1.png",
    },
]

export default function CaveOfTrialsGuide() {
    return (
        <div className="min-h-screen bg-slate-950 pt-32 pb-24">
            <div className="container mx-auto px-4 max-w-5xl">
                {/* Back Link */}
                <Link href="/guides" className="inline-flex items-center gap-2 text-slate-500 hover:text-amber-500 transition-colors mb-12 group">
                    <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold uppercase tracking-widest leading-none">Back to Guides</span>
                </Link>

                {/* Hero section */}
                <div className="relative mb-20">
                    <div className="absolute -top-24 -left-24 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" />
                    <div className="relative border-b border-slate-800 pb-12">
                        <div className="flex items-center gap-3 text-amber-500 mb-6">
                            <MapPin className="w-4 h-4" />
                            <span className="text-[10px] font-black uppercase tracking-[0.3em]">Aden Region • Weekly Instance</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter uppercase italic font-cinzel">
                            Cave of Trials <br />
                            <span className="text-amber-500">Execution Guide</span>
                        </h1>
                        <p className="text-slate-400 text-xl leading-relaxed max-w-3xl italic font-sans mb-8">
                            Master the solo instance of Cave of Trials. Learn how to navigate the quest chain, rescue captives, and claim your weekly power-up.
                        </p>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                                <Sword className="w-4 h-4 text-amber-500" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none mb-1">Created By</p>
                                <p className="text-sm font-black text-amber-500 uppercase tracking-wider leading-none">DagDoom</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reward Callout */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-amber-500/20 to-amber-700/20 border border-amber-500/30 rounded-3xl p-8 mb-20 flex flex-col md:flex-row items-center gap-8 shadow-2xl shadow-amber-900/10"
                >
                    <div className="w-20 h-20 rounded-2xl bg-amber-500 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/40">
                        <Award className="w-10 h-10 text-slate-950" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-white uppercase italic font-cinzel mb-2">Grand Prize: Weekly Power-up</h2>
                        <p className="text-amber-100/70 italic leading-relaxed">
                            Completing the Cave of Trials grants a unique <span className="text-amber-400 font-bold">7-day buff</span> that significantly boosts your combat capabilities in all farm zones. This instance can be repeated weekly for continuous empowerment.
                        </p>
                    </div>
                    <div className="md:ml-auto shrink-0 flex items-center gap-3 px-4 py-2 bg-slate-950/40 rounded-full border border-white/5">
                        <Clock className="w-4 h-4 text-amber-400" />
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">7 Day Duration</span>
                    </div>
                </motion.div>

                {/* Steps */}
                <div className="space-y-12 relative mb-24">
                    {/* Vertical line connector */}
                    <div className="absolute left-10 md:left-1/2 top-0 bottom-0 w-px bg-slate-800 -translate-x-1/2 hidden md:block" />

                    {steps.map((step, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className={`flex flex-col md:flex-row items-center gap-12 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
                        >
                            <div className="w-full md:w-1/2 space-y-6">
                                <div className={`flex items-center gap-4 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                                    <div className="w-20 h-20 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center group shrink-0 relative">
                                        <div className={`absolute inset-0 bg-amber-500/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity`} />
                                        <step.icon className={`w-8 h-8 text-amber-500 group-hover:scale-110 transition-transform`} />
                                        <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-950 border border-amber-500/30 flex items-center justify-center text-[10px] font-black text-amber-500">
                                            0{i + 1}
                                        </div>
                                    </div>
                                    <div className={`${i % 2 !== 0 ? 'md:text-right' : ''}`}>
                                        <h3 className="text-2xl font-black text-white uppercase italic font-cinzel leading-none mb-2">{step.title}</h3>
                                        <div className={`flex items-center gap-4 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{step.location}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={`p-8 rounded-3xl bg-slate-900/30 border border-slate-800/50 hover:border-amber-500/20 transition-colors ${i % 2 !== 0 ? 'md:text-right shadow-[-20px_20px_40px_rgba(0,0,0,0.3)]' : 'shadow-[20px_20px_40px_rgba(0,0,0,0.3)]'}`}>
                                    {step.image && (
                                        <div className="relative w-full h-48 mb-6 rounded-xl overflow-hidden border border-slate-800/50 group-hover:border-amber-500/30 transition-colors">
                                            <Image
                                                src={step.image}
                                                alt={step.title}
                                                fill
                                                className="object-cover opacity-70 hover:opacity-100 transition-opacity duration-300"
                                            />
                                        </div>
                                    )}
                                    <p className="text-slate-400 italic leading-relaxed">
                                        {step.description}
                                    </p>
                                    <div className={`mt-6 pt-6 border-t border-slate-800/50 flex items-center gap-3 ${i % 2 !== 0 ? 'justify-end' : ''}`}>
                                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Speak to:</span>
                                        <span className="text-[10px] font-black text-amber-500 uppercase tracking-wider">{step.npc}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="hidden md:block w-4 h-4 rounded-full bg-amber-500 border-4 border-slate-950 relative z-10 shadow-lg shadow-amber-500/20" />
                            <div className="w-full md:w-1/2" />
                        </motion.div>
                    ))}
                </div>

                {/* Conclusion */}
                <div className="text-center p-12 rounded-[3rem] bg-slate-900/40 border border-slate-800 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none group-hover:scale-125 group-hover:opacity-10 transition-all duration-700">
                        <Sword className="w-64 h-64 text-amber-500" />
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-4xl font-black text-white uppercase italic font-cinzel mb-6">Ready for the Trial?</h3>
                        <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto italic">
                            The Cave of Trials awaits your presence. Ensure you are well-prepared with your best gear before entering.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-6">
                            <Link href="/">
                                <Button className="bg-amber-600 hover:bg-amber-500 text-white font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg shadow-amber-900/30">
                                    Join the Battle
                                </Button>
                            </Link>
                            <Link href="/guides">
                                <Button variant="outline" className="border-slate-800 text-slate-400 hover:text-amber-500 px-10 h-14 rounded-full font-bold uppercase tracking-widest">
                                    Explore More Guides
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
